/* Canvas helpers shared by every template.

   These exist because the poster is composed in the browser rather than
   by an image library on the server: the preview and the downloaded PNG
   come out of the same code, so what you see cannot drift from what you
   post. Canvas gives us gradients and font metrics for free; what it
   does not give us is letter-spacing, text that fits its box, or any
   sharpening after an upscale, so those are here. */

export type Ctx = CanvasRenderingContext2D;

/** A font at a given size: `anton(196)` → '400 196px Anton, …'. */
export type FontAt = (px: number) => string;

/* ── text ─────────────────────────────────────────────────────────── */

/** Width of `text` once `track` px of letter-spacing is added. */
export function trackedWidth(ctx: Ctx, text: string, track: number): number {
  if (!text) return 0;
  let w = 0;
  for (const ch of text) w += ctx.measureText(ch).width + track;
  return w - track;
}

/**
 * Draw text with letter-spacing, character by character.
 * `ctx.letterSpacing` exists but is not in every browser we might be
 * opened in, and it does not report the trailing space consistently —
 * doing it by hand keeps the measurement and the drawing in agreement.
 * Returns the width drawn.
 */
export function tracked(ctx: Ctx, x: number, y: number, text: string, track: number): number {
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + track;
  }
  return cx - x - (text ? track : 0);
}

/**
 * The largest size at or below `max` at which `text` fits `maxW`.
 * Headlines are typed by hand, so "SEALED" and "COMMANDER-KVÖLD" both
 * have to work without anyone thinking about point sizes.
 */
export function fitSize(
  ctx: Ctx,
  text: string,
  maxW: number,
  font: FontAt,
  max: number,
  min: number,
  track = 0,
): number {
  for (let px = max; px > min; px -= 1) {
    ctx.font = font(px);
    const w = track ? trackedWidth(ctx, text, track) : ctx.measureText(text).width;
    if (w <= maxW) return px;
  }
  return min;
}

/** Distance from the baseline to the top of the actual glyphs. */
export function capAscent(ctx: Ctx, text: string): number {
  return ctx.measureText(text).actualBoundingBoxAscent;
}

/* ── colour ───────────────────────────────────────────────────────── */

export const rgba = (c: [number, number, number], a: number): string =>
  `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${a})`;

/** A vertical wash of one colour, given [position, alpha] stops. */
export function verticalScrim(
  ctx: Ctx,
  x: number,
  y: number,
  w: number,
  h: number,
  colour: [number, number, number],
  stops: [number, number][],
): void {
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  for (const [t, a] of stops) g.addColorStop(t, rgba(colour, a));
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
}

/**
 * Darkening toward the left and right edges, so the picture sits down
 * and the type at the margins keeps its contrast. Built from the curve
 * alpha = peak * ((t - hold) / (1 - hold))^1.8, sampled into stops.
 */
export function edgeVignette(
  ctx: Ctx,
  w: number,
  h: number,
  colour: [number, number, number],
  peak = 0.55,
  hold = 0.55,
): void {
  const g = ctx.createLinearGradient(0, 0, w, 0);
  // The curve is flat across the middle and only bends near the edges,
  // so stops spread evenly waste almost all of themselves on the flat
  // part and leave the bend visibly faceted. 64 is enough that the
  // active quarter still gets ~16 each side.
  const STEPS = 64;
  for (let i = 0; i <= STEPS; i++) {
    const p = i / STEPS;
    const t = Math.abs(p - 0.5) / 0.5;
    const a = peak * Math.max(0, (t - hold) / (1 - hold)) ** 1.8;
    g.addColorStop(p, rgba(colour, a));
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

/* ── pictures ─────────────────────────────────────────────────────── */

const clamp255 = (n: number): number => (n < 0 ? 0 : n > 255 ? 255 : n);

/**
 * Unsharp mask, in place.
 *
 * Card art from Scryfall is 633px wide — that is the largest art-only
 * image they publish — so filling a 1080px poster always means an
 * upscale, and an upscale without sharpening reads as mush. Amount is
 * tied to how far the picture was stretched.
 */
function unsharp(cv: HTMLCanvasElement, amount: number, radius: number): void {
  const w = cv.width;
  const h = cv.height;
  if (w === 0 || h === 0) return;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;

  let sharp: ImageData;
  try {
    sharp = ctx.getImageData(0, 0, w, h);
  } catch {
    // A cross-origin picture taints the canvas. Everything we draw is
    // same-origin, but never fail the whole render over sharpening.
    return;
  }

  const blurCv = document.createElement('canvas');
  blurCv.width = w;
  blurCv.height = h;
  const bctx = blurCv.getContext('2d', { willReadFrequently: true });
  if (!bctx) return;
  bctx.filter = `blur(${radius}px)`;
  bctx.drawImage(cv, 0, 0);
  const blurred = bctx.getImageData(0, 0, w, h).data;

  const px = sharp.data;
  for (let i = 0; i < px.length; i += 4) {
    px[i] = clamp255(px[i]! + amount * (px[i]! - blurred[i]!));
    px[i + 1] = clamp255(px[i + 1]! + amount * (px[i + 1]! - blurred[i + 1]!));
    px[i + 2] = clamp255(px[i + 2]! + amount * (px[i + 2]! - blurred[i + 2]!));
  }
  ctx.putImageData(sharp, 0, 0);
}

export type PictureFit = {
  /** Extra magnification over "fill the width", as a multiplier. */
  zoom: number;
  /** 0 shows the top of the picture, 1 the bottom. Only bites when the
      scaled picture is taller than the band. */
  focus: number;
};

/**
 * Paint `img` across the top `bandH` of the canvas.
 *
 * The picture is scaled exactly once, straight to its final size, and
 * sharpened there. Composing at 2x and shrinking afterwards — the
 * obvious way to get crisp text — costs the artwork a second resample
 * and visibly softens it, so the type is drawn at final size instead
 * and left to the browser's own antialiasing.
 */
/* Scaling and sharpening a picture costs tens of milliseconds, which is
   too much to repeat on every keystroke in the editor. The result only
   depends on the picture and how it is framed, so the last one is kept
   and reused while the text is being typed. */
let bandCache: { key: string; canvas: HTMLCanvasElement } | null = null;

export function drawPicture(
  ctx: Ctx,
  img: HTMLImageElement,
  w: number,
  bandH: number,
  { zoom, focus }: PictureFit,
): void {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;

  const key = `${img.src.slice(-96)}|${iw}x${ih}|${w}x${bandH}|${zoom}|${focus}`;
  if (bandCache?.key === key) {
    ctx.drawImage(bandCache.canvas, 0, 0);
    return;
  }

  const scale = (w / iw) * zoom;
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = (w - dw) / 2;
  // Taller than the band: slide it. Shorter: sit it at the top and let
  // the scrim carry the picture down into the black.
  const dy = dh > bandH ? -(dh - bandH) * focus : 0;

  const off = document.createElement('canvas');
  off.width = Math.round(w);
  off.height = Math.round(bandH);
  const octx = off.getContext('2d', { willReadFrequently: true });
  if (!octx) return;
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(img, dx, dy, dw, dh);

  if (scale > 1.15) {
    // Ramp with the stretch, but stop before it turns crunchy.
    unsharp(off, Math.min(0.9, (scale - 1) * 0.75), 1.5);
  }
  bandCache = { key, canvas: off };
  ctx.drawImage(off, 0, 0);
}

/* ── loading ──────────────────────────────────────────────────────── */

/** Load an image from a URL or data URL. Never rejects — null on failure. */
export function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Make sure every font a template needs is actually available.
 *
 * Canvas does not wait for webfonts and does not tell you it has
 * substituted one — it just draws Arial. For a poster that gets
 * downloaded and posted, that is worth blocking on.
 */
export async function ensureFonts(specs: string[]): Promise<string[]> {
  await Promise.all(
    specs.map((s) => document.fonts.load(s).catch(() => undefined)),
  );
  try {
    await document.fonts.ready;
  } catch {
    /* older browsers */
  }
  return specs.filter((s) => !document.fonts.check(s));
}
